import { createStore } from "vuex";
import { db } from "@/firebaseConfig";
import {
  collection,
  getDocs,
  getDoc,
  doc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  startAfter,
  limit,
  serverTimestamp,
  deleteField,
  arrayUnion,
  arrayRemove,
  documentId,
  startAt,
  endAt,
} from "firebase/firestore";
import sermon from "@/stores/modules/sermon";
import users from "@/stores/modules/users";
import teams from "@/stores/modules/teams";
import events from "@/stores/modules/events";
import roles from "@/stores/modules/roles";
import organisations from "@/stores/modules/organisations";
import auth from "@/stores/modules/auth";
import memberships from "@/stores/modules/memberships";
import eventTeams from "@/stores/modules/eventTeams";
import scheduleTemplates from "@/stores/modules/scheduleTemplates";
import slideshows from "@/stores/modules/slideshows";
import slideImages from "@/stores/modules/slideImages";
import songVariants from "@/stores/modules/songVariants";
import songCatalogue from "@/stores/modules/songCatalogue";
import { songConverter } from "@/composables/useConverters";
import { SONGS_PAGE_SIZE, buildPageCursors } from "@/firebase/pageCursors";
import { SongChapterData, SongModel } from "@/models/interfaces";

const normalizeSong = <T extends { data?: unknown } | null | undefined>(
  song: T,
): T => {
  if (!song || Array.isArray(song.data)) return song;

  if (song.data && typeof song.data === "object") {
    const chapters = Object.entries(song.data as Record<string, unknown>)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([, value]) => value)
      .filter(
        (value): value is SongChapterData =>
          !!value && typeof value === "object" && "id" in value,
      );

    return { ...song, data: chapters } as T;
  }

  return { ...song, data: [] } as T;
};

let activeSongRequestVersion = 0;
let songsRequestVersion = 0;

const store = createStore<any>({
  state: {
    activeEvent: {
      data: null,
    },
    activeParticipants: {
      data: null,
    },
    activeSong: {
      data: null,
    },
    darkMode: false,
    documentBackgroundColor: null as string | null,
    documentBackgroundDarkColor: null as string | null,
    fetchingEvent: {
      value: false,
    },
    fetchingSchedule: {
      value: false,
    },
    fetchingParticipants: {
      value: false,
    },
    fetchingSong: false,
    fetchingSongs: {
      value: false,
    },
    fetchingTeams: {
      value: false,
    },
    isFirebaseReady: false,
    keys: [
      {
        key: 0,
        index: 0,
        value: "C",
        note: "C",
        notes: ["C", "D", "E", "F", "G", "A", "B"],
        chords: [
          { primary: 1 },
          { primary: 2, minor: true },
          { primary: 3, minor: true },
          { primary: 4 },
          { primary: 5 },
          { primary: 6, minor: true },
          { primary: 7, add: "°" },
        ],
      },
      {
        key: 1,
        index: 1,
        value: "Db",
        note: "Db",
        notes: ["Db", "Eb", "F", "Gb", "Ab", "Bb", "C"],
        chords: [
          { primary: 1 },
          { primary: 2, minor: true },
          { primary: 3, minor: true },
          { primary: 4 },
          { primary: 5 },
          { primary: 6, minor: true },
          { primary: 7, add: "°" },
        ],
      },
      {
        key: 2,
        index: 2,
        value: "D",
        note: "D",
        notes: ["D", "E", "F#", "G", "A", "B", "C#"],
        chords: [
          { primary: 1 },
          { primary: 2, minor: true },
          { primary: 3, minor: true },
          { primary: 4 },
          { primary: 5 },
          { primary: 6, minor: true },
          { primary: 7, add: "°" },
        ],
      },
      {
        key: 3,
        index: 3,
        value: "Eb",
        note: "Eb",
        notes: ["Eb", "F", "G", "Ab", "Bb", "C", "D"],
        chords: [
          { primary: 1 },
          { primary: 2, minor: true },
          { primary: 3, minor: true },
          { primary: 4 },
          { primary: 5 },
          { primary: 6, minor: true },
          { primary: 7, add: "°" },
        ],
      },
      {
        key: 4,
        index: 4,
        value: "E",
        note: "E",
        notes: ["E", "F#", "G#", "A", "B", "C#", "D#"],
        chords: [
          { primary: 1 },
          { primary: 2, minor: true },
          { primary: 3, minor: true },
          { primary: 4 },
          { primary: 5 },
          { primary: 6, minor: true },
          { primary: 7, add: "°" },
        ],
      },
      {
        key: 5,
        index: 5,
        value: "F",
        note: "F",
        notes: ["F", "G", "A", "Bb", "C", "D", "E"],
        chords: [
          { primary: 1 },
          { primary: 2, minor: true },
          { primary: 3, minor: true },
          { primary: 4 },
          { primary: 5 },
          { primary: 6, minor: true },
          { primary: 7, add: "°" },
        ],
      },
      {
        key: 6,
        index: 6,
        value: "F#",
        note: "F#",
        notes: ["F#", "G#", "A#", "B", "C#", "D#", "F"],
        chords: [
          { primary: 1 },
          { primary: 2, minor: true },
          { primary: 3, minor: true },
          { primary: 4 },
          { primary: 5 },
          { primary: 6, minor: true },
          { primary: 7, add: "°" },
        ],
      },
      {
        key: 7,
        index: 7,
        value: "G",
        note: "G",
        notes: ["G", "A", "B", "C", "D", "E", "F#"],
        chords: [
          { primary: 1 },
          { primary: 2, minor: true },
          { primary: 3, minor: true },
          { primary: 4 },
          { primary: 5 },
          { primary: 6, minor: true },
          { primary: 7, add: "°" },
        ],
      },
      {
        key: 8,
        index: 8,
        value: "Ab",
        note: "Ab",
        notes: ["Ab", "Bb", "C", "Db", "Eb", "F", "G"],
        chords: [
          { primary: 1 },
          { primary: 2, minor: true },
          { primary: 3, minor: true },
          { primary: 4 },
          { primary: 5 },
          { primary: 6, minor: true },
          { primary: 7, add: "°" },
        ],
      },
      {
        key: 9,
        index: 9,
        value: "A",
        note: "A",
        notes: ["A", "B", "C#", "D", "E", "F#", "G#"],
        chords: [
          { primary: 1 },
          { primary: 2, minor: true },
          { primary: 3, minor: true },
          { primary: 4 },
          { primary: 5 },
          { primary: 6, minor: true },
          { primary: 7, add: "°" },
        ],
      },
      {
        key: 10,
        index: 10,
        value: "Bb",
        note: "Bb",
        notes: ["Bb", "C", "D", "Eb", "F", "G", "A"],
        chords: [
          { primary: 1 },
          { primary: 2, minor: true },
          { primary: 3, minor: true },
          { primary: 4 },
          { primary: 5 },
          { primary: 6, minor: true },
          { primary: 7, add: "°" },
        ],
      },
      {
        key: 11,
        index: 11,
        value: "B",
        note: "B",
        notes: ["B", "C#", "D#", "E", "F#", "G#", "A#"],
        chords: [
          { primary: 1 },
          { primary: 2, minor: true },
          { primary: 3, minor: true },
          { primary: 4 },
          { primary: 5 },
          { primary: 6, minor: true },
          { primary: 7, add: "°" },
        ],
      },
    ],
    popupMode: {
      data: false,
    },
    selectedDay: {
      data: null,
    },
    skills: {
      data: null,
    },
    songs: {
      data: null,
    },
    songsFromSearch: {
      data: null,
    },
    cursors: {
      value: {},
    },
    upcomingEvent: {
      data: null,
    },
    versions: {
      data: {},
    },
  },
  getters: {
    activeEvent(state: any) {
      return state.activeEvent.data;
    },
    activeParticipants(state: any) {
      return state.activeParticipants.data;
    },
    activeSong(state: any) {
      return state.activeSong.data;
    },
    cursors(state: any) {
      return state.cursors.value;
    },
    eventIsPublic(state: any) {
      return false;
    },
    fetchingSong(state: any) {
      return state.fetchingSong;
    },
    fetchingSongs(state: any) {
      return state.fetchingSongs.value;
    },
    fetchingParticipants(state: any) {
      return state.fetchingParticipants.data;
    },
    fetchingTeams(state: any) {
      return state.fetchingTeams.value;
    },
    isDarkMode(state: any) {
      return state.darkMode;
    },
    isFirebaseReady(state: any) {
      return state.isFirebaseReady;
    },
    keys(state: any) {
      return state.keys;
    },
    popupMode(state: any) {
      return state.popupMode;
    },
    selectedDay(state: any) {
      return state.selectedDay;
    },
    skills(state: any) {
      return state.skills;
    },
    songs(state: any) {
      return state.songs;
    },
    songsFromSearch(state: any) {
      return state.songsFromSearch.data;
    },
    upcomingEvent(state: any) {
      return state.upcomingEvent.data;
    },
    versions(state: any) {
      return state.versions.data;
    },
  },
  mutations: {
    ADD_EVENTS(state: any, value: any) {
      if (state.events?.data) {
        state.events.data = [...new Set([...state.events.data, ...value])];
      } else {
        if (!state.events) state.events = { data: null };
        state.events.data = value;
      }
    },
    SET_ACTIVE_PARTICIPANTS(state: any, value: any) {
      state.activeParticipants.data = value;
    },
    SET_DOCUMENT_BACKGROUND_COLOR(state: any, value: string | null) {
      state.documentBackgroundColor = value;
    },
    SET_DOCUMENT_BACKGROUND_DARK_COLOR(state: any, value: string | null) {
      state.documentBackgroundDarkColor = value;
    },
    SET_DARKMODE(state: any, value: boolean) {
      state.darkMode = value;
    },
    SET_EVENTS(state: any, value: any) {
      if (!state.events) state.events = { data: null };
      state.events.data = value;
    },
    SET_EVENT(state: any, value: any) {
      state.activeEvent.data = value;
    },
    SET_FETCHING_EVENT(state: any, value: boolean) {
      state.fetchingEvent.value = value;
    },
    SET_FETCHING_SCHEDULE(state: any, value: boolean) {
      state.fetchingSchedule.value = value;
    },
    SET_FETCHING_PARTICIPANTS(state: any, value: boolean) {
      state.fetchingParticipants.value = value;
    },
    SET_FETCHING_TEAMS(state: any, value: boolean) {
      state.fetchingTeams.value = value;
    },
    SET_FETCHING_SONG(state: any, value: boolean) {
      state.fetchingSong = value;
    },
    SET_FETCHING_SONGS(state: any, value: boolean) {
      state.fetchingSongs.value = value;
    },
    SET_FIREBASE_READY(state: any, value: boolean) {
      state.isFirebaseReady = value;
    },
    SET_POPUPMODE(state: any, payload: boolean) {
      state.popupMode.data = payload;
    },
    SET_SELECTED_DAY(state: any, value: any) {
      state.selectedDay.data = value;
    },
    SET_SONG(state: any, value: any) {
      state.activeSong.data = normalizeSong(value);
    },
    SET_SONGS(state: any, value: any) {
      state.songs.data = Array.isArray(value) ? value.map(normalizeSong) : value;
    },
    SET_SONGS_FROM_SEARCH(state: any, items: any) {
      state.songsFromSearch.data = Array.isArray(items)
        ? items.map(normalizeSong)
        : items;
    },
    SET_TEAMS(state: any, value: any) {
      if (!state.teams) state.teams = { data: null };
      state.teams.data = value;
      state.fetchingTeams.value = false;
    },
    SET_USERS_IN_TEAM(state: any, payload: { users: any[]; teamId: string }) {
      const users = payload.users;
      state.teams.data = state.teams.data.map((team: any) =>
        team.id === payload.teamId ? { ...team, users } : team,
      );
    },
    UPDATE_CURSORS(
      state: any,
      payload: { collection: string; field: string; cursor: any },
    ) {
      state.cursors.value[`${payload.collection}_${payload.field}`] =
        payload.cursor;
    },
    SET_UPCOMING_EVENT(state: any, value: any) {
      state.upcomingEvent.data = value;
    },
    UPDATE_ACTIVE_SCHEDULE_SONG_KEY(
      state: any,
      payload: { scheduleId: string; key?: any },
    ) {
      if (!payload.key) return;
      const scheduleItem = state.activeSchedule.data.find(
        (el: any) => el.id == payload.scheduleId,
      );
      if (scheduleItem) {
        scheduleItem.key = payload.key;
      }
    },
    UPDATE_SCHEDULE_ELEMENT(
      state: any,
      payload: { scheduleId: string; start?: Date | any },
    ) {
      if (state.activeSchedule?.data && payload.start) {
        const target = state.activeSchedule.data.find(
          (item: any) => item.id === payload.scheduleId,
        );
        if (target) {
          const ms = new Date(payload.start).getTime();
          target.start.seconds = Math.floor(ms / 1000);
        }
      }
    },
    UPDATE_ACTIVE_SONG_DATA(state: any, value: any) {
      state.activeSong.data.data[value.index] = value;
    },
    UPDATE_ACTIVE_SONG_CONTENT(state: any, value: any) {
      state.activeSong.data.data[value.index].content = value.content;
      if ("lyrics" in value) {
        state.activeSong.data.data[value.index].lyrics = value.lyrics;
      }
    },
    UPDATE_USER_SKILLS(state: any, payload: any) {
      const user = state.users.data.find((el: any) => el.id == payload.userId);
      if (!user.data.skills) {
        user.data.skills = [];
      }
      user.data.skills.push({
        id: payload.skill.id,
        title: payload.skill.title,
      });
    },
    UPDATE_USER_TEAMS(state: any, payload: any) {
      const user = state.users.data.find((el: any) => el.id == payload.userId);
      if (user) {
        if (!Array.isArray(user.teams)) user.teams = [];

        if (!user.teams.some((t: any) => t.id === payload.team.id)) {
          user.teams.push(payload.team.id);
        }
      }
    },
    REMOVE_SKILL_FROM_USER(state: any, payload: any) {
      const user = state.users.data.find((el: any) => el.id == payload.userId);
      const newSkills = user.data.skills.filter(
        (item: any) => item.id != payload.skill.id,
      );
      user.data.skills = newSkills;
    },
    REMOVE_TEAM_FROM_USER(state: any, payload: any) {
      const user = state.users.data.find((el: any) => el.id == payload.userId);
      const newTeams = user.data.teams.filter(
        (item: any) => item.id != payload.team.id,
      );
      user.data.teams = newTeams;
    },
    SET_VERSION(state: any, payload: { target: string; value: any }) {
      state.versions.data[payload.target] = payload.value;
    },
  },
  actions: {
    async addLowercaseTitlesToSongs() {
      try {
        const songsCol = collection(db, "songs");
        const snapshot = await getDocs(songsCol);

        const updates: Promise<void>[] = [];

        snapshot.forEach((songDoc) => {
          const data = songDoc.data();
          // A variant is kept out of the song lists by having no
          // title_lowercase (see the songVariants module).
          if (data.title && !data.variantOf) {
            const songRef = doc(db, "songs", songDoc.id);
            updates.push(
              updateDoc(songRef, {
                title_lowercase: data.title.toLowerCase(),
              }),
            );
          }
        });

        await Promise.all(updates);
      } catch (err) {
        console.error("Error updating songs with lowercase titles:", err);
      }
    },

    async setDarkMode(context: any, payload: { darkMode: boolean }) {
      if (payload.darkMode) {
        document.body.classList.add("dark");
      } else {
        document.body.classList.remove("dark");
      }
      localStorage.setItem("darkMode", String(payload.darkMode));
      context.commit("SET_DARKMODE", payload.darkMode);
      store.dispatch("updateDocumentBackgroundColor");
    },

    async getVersion(
      context: any,
      payload: { target: string; callback?: () => void },
    ) {
      const docRef = doc(db, "versions", payload.target);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        context.commit("SET_VERSION", {
          target: payload.target,
          value: docSnap.data().version,
        });
        if (payload.callback) payload.callback();
      }
    },

    updateDocumentBackgroundColor(
      context: any,
      payload?: { color?: string; darkColor?: string },
    ) {
      const defaultColor = "#FF8800";
      const defaultDarkColor = "#FF8800";

      if (payload?.color) {
        context.commit("SET_DOCUMENT_BACKGROUND_COLOR", payload.color);
      }
      if (payload?.darkColor) {
        context.commit("SET_DOCUMENT_BACKGROUND_DARK_COLOR", payload.darkColor);
      }

      const light = defaultColor;
      const dark = defaultDarkColor;
      const finalColor = context.getters.isDarkMode
        ? (payload?.darkColor ?? dark)
        : (payload?.color ?? light);

      let theme = document.querySelector(
        'meta[name="theme-color"]:not([media])',
      ) as HTMLMetaElement | null;
      if (!theme) {
        theme = document.createElement("meta");
        theme.setAttribute("name", "theme-color");
        document.head.appendChild(theme);
      }
      theme.setAttribute("content", finalColor);

      let themeLight = document.querySelector(
        'meta[name="theme-color"][media="(prefers-color-scheme: light)"]',
      ) as HTMLMetaElement | null;
      if (!themeLight) {
        themeLight = document.createElement("meta");
        themeLight.setAttribute("name", "theme-color");
        themeLight.setAttribute("media", "(prefers-color-scheme: light)");
        document.head.appendChild(themeLight);
      }
      themeLight.setAttribute("content", light);

      let themeDark = document.querySelector(
        'meta[name="theme-color"][media="(prefers-color-scheme: dark)"]',
      ) as HTMLMetaElement | null;
      if (!themeDark) {
        themeDark = document.createElement("meta");
        themeDark.setAttribute("name", "theme-color");
        themeDark.setAttribute("media", "(prefers-color-scheme: dark)");
        document.head.appendChild(themeDark);
      }
      themeDark.setAttribute("content", dark);
    },

    // SKILLS

    async fetchSkills(context: any) {
      const qRef = query(
        collection(db, "skills"),
        orderBy("title"),
        where("account", "==", Number(store.getters["users/user"].account)),
      );
      const querySnapshot = await getDocs(qRef);
      const arr: any[] = [];
      querySnapshot.forEach((d) => {
        arr.push({
          id: d.id,
          ...d.data(),
        });
      });
      context.commit("SET_SKILLS", arr);
    },

    async createSkill(
      context: any,
      payload: { data: any; callback?: () => void },
    ) {
      const docRef = await addDoc(collection(db, "skills"), payload.data);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const obj = {
          id: docRef.id,
          data: docSnap.data(),
        };
        const skillsClone = JSON.parse(
          JSON.stringify(context.getters.skills.data || []),
        );
        skillsClone.push(obj);
        skillsClone.sort((a: any, b: any) =>
          a.data.title.localeCompare(b.data.title),
        );
        context.commit("SET_SKILLS", skillsClone);
      }

      if (payload.callback) payload.callback();
    },

    async addSkillToUser(context: any, payload: any) {
      const userRef = doc(db, "users", payload.userId);

      updateDoc(userRef, {
        skills: arrayUnion(payload.skill),
      })
        .then(() => {
          context.commit("UPDATE_USER_SKILLS", payload);
          if (payload.callback) payload.callback();
        })
        .catch((err) => {
          console.error(err);
        });
    },

    async removeSkillFromUser(context: any, payload: any) {
      const userRef = doc(db, "users", payload.userId);

      updateDoc(userRef, {
        skills: arrayRemove(payload.skill),
      })
        .then(() => {
          context.commit("REMOVE_SKILL_FROM_USER", payload);
          if (payload.callback) payload.callback();
        })
        .catch((err) => {
          console.error(err);
        });
    },

    async createTeam(
      context: any,
      payload: { data: any; callback?: () => void },
    ) {
      const docRef = await addDoc(collection(db, "teams"), payload.data);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const obj = {
          id: docRef.id,
          ...docSnap.data(),
        };
        const clone = JSON.parse(
          JSON.stringify(context.getters.teams.data || []),
        );
        clone.push(obj);
        clone.sort((a: any, b: any) =>
          a.data.title.localeCompare(b.data.title),
        );
        context.commit("SET_TEAMS", clone);
      }

      if (payload.callback) payload.callback();
    },

    async addTeamToUser(context: any, payload: any) {
      const teamRef = doc(db, "teams", payload.teamId);
      const userRef = doc(db, "users", payload.userId);

      try {
        await updateDoc(userRef, {
          teams: arrayUnion(teamRef),
        });

        context.commit("UPDATE_USER_TEAMS", {
          userId: payload.userId,
          team: teamRef,
        });

        const state = context.state;

        const team = state.teams.data.find((t: any) => t.id === payload.teamId);
        const currentUsers = Array.isArray(team?.users) ? team.users : [];

        const user = state.users.data.find((u: any) => u.id === payload.userId);
        if (!user) {
          if (payload.callback) payload.callback();
          return;
        }

        const updatedUser = {
          ...user,
          teams: Array.isArray(user.teams)
            ? user.teams.some((t: any) => t.id === teamRef.id)
              ? user.teams
              : [...user.teams, teamRef]
            : [teamRef],
        };

        const nextUsers = (() => {
          const idx = currentUsers.findIndex(
            (u: any) => u.id === updatedUser.id,
          );
          if (idx === -1) return [...currentUsers, updatedUser];
          const copy = currentUsers.slice();
          copy[idx] = { ...currentUsers[idx], ...updatedUser };
          return copy;
        })();

        context.commit("SET_USERS_IN_TEAM", {
          teamId: payload.teamId,
          users: nextUsers,
        });
        if (payload.callback) payload.callback();
        return { result: "success" };
      } catch (err) {
        console.error(err);
      }
    },

    async addUserToTeam(context: any, payload: any) {
      const teamRef = doc(db, "teams", payload.teamId);
      const userRef = doc(db, "users", payload.userId);

      try {
        updateDoc(userRef, {
          teams: arrayUnion(teamRef),
        }).then(() => {});
      } catch (error) {
        console.error(error);
      }
    },

    async removeTeamFromUser(context: any, payload: any) {
      const userRef = doc(db, "users", payload.userId);

      updateDoc(userRef, {
        teams: arrayRemove(payload.team),
      })
        .then(() => {
          context.commit("REMOVE_TEAM_FROM_USER", payload);
          if (payload.callback) payload.callback();
        })
        .catch((err) => {
          console.error(err);
        });
    },

    async getAllEvents(context: any) {
      const qRef = query(
        collection(db, "events"),
        where("account", "==", Number(store.getters["users/user"].account)),
      );

      const querySnapshot = await getDocs(qRef);
      const d: any[] = [];
      querySnapshot.forEach((dSnap) => {
        d.push({
          id: dSnap.id,
          ...dSnap.data(),
        });
      });
      context.commit("SET_EVENTS", d);
    },

    async getMonthEvents(context: any, payload: { month: Date }) {
      const startDate = new Date(payload.month);
      startDate.setDate(1);
      startDate.setHours(0);

      const endDate = new Date(
        payload.month.getFullYear(),
        payload.month.getMonth() + 1,
        0,
        23,
        59,
        59,
        999,
      );

      const qRef = query(
        collection(db, "events"),
        where("account", "==", Number(store.getters["users/user"].account)),
        where("start", ">", startDate),
        where("start", "<", endDate),
      );

      const querySnapshot = await getDocs(qRef);

      const d = querySnapshot.docs.map((dSnap) => ({
        ...dSnap.data(),
        id: dSnap.id,
      }));

      context.commit("ADD_EVENTS", d);
    },

    async fetchUpcomingEvent(context: any) {
      const now = new Date();
      const qRef = query(
        collection(db, "events"),
        where("account", "==", Number(store.getters["users/user"].account)),
        where("start", ">", now),
        limit(1),
      );
      const snapshot = await getDocs(qRef);

      if (!snapshot.empty) {
        const d = snapshot.docs[0];
        context.commit("SET_UPCOMING_EVENT", {
          id: d.id,
          ...d.data(),
        });
      }
    },

    async fetchEvent(
      context: any,
      payload: { id: string; callback?: () => void },
    ) {
      context.commit("SET_FETCHING_EVENT", true);
      await onSnapshot(doc(db, "events", payload.id), (evt) => {
        const obj = {
          id: evt.id,
          ...evt.data(),
        };
        context.commit("SET_EVENT", obj);
        context.commit("SET_FETCHING_EVENT", false);
        if (payload.callback) payload.callback();
      });
    },

    async createEvent(
      context: any,
      payload: { data: any; callback: () => void },
    ) {
      await addDoc(collection(db, "events"), payload.data);
      payload.callback();
    },

    async setSelectedDay(context: any, payload: any) {
      context.commit("SET_SELECTED_DAY", payload);
    },

    async setActiveEvent(context: any, payload: any) {
      context.commit("SET_ACTIVE_SCHEDULE", null);
      context.commit("SET_EVENT", payload);
    },

    async fetchSchedule(
      context: any,
      payload: { docId: string; callback?: () => void },
    ) {
      context.commit("SET_FETCHING_SCHEDULE", true);
      try {
        const collectionRef = collection(
          db,
          "events",
          payload.docId,
          "schedule",
        );
        const snapshot = await getDocs(collectionRef);
        const schedule = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (schedule.length > 0)
          context.commit("SET_ACTIVE_SCHEDULE", schedule);
      } finally {
        context.commit("SET_FETCHING_SCHEDULE", false);
        if (payload.callback) payload.callback();
      }
    },

    async fetchEventParticipants(
      context: any,
      payload: { docId: string; callback?: () => void },
    ) {
      context.commit("SET_FETCHING_PARTICIPANTS", true);

      const collectionRef = collection(
        db,
        "events",
        payload.docId,
        "participants",
      );
      const snapshot = await getDocs(collectionRef);
      const result = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      if (result.length > 0) context.commit("SET_ACTIVE_PARTICIPANTS", result);
      context.commit("SET_FETCHING_PARTICIPANTS", false);
      if (payload.callback) payload.callback();
    },

    async getAttendees(
      context: any,
      payload: { id: string; callback?: () => void },
    ) {
      const attendingSnapRef = collection(
        db,
        "events",
        payload.id,
        "attending",
      );
      const attendingSnap = await getDocs(attendingSnapRef);
      const notAttendingSnapRef = collection(
        db,
        "events",
        payload.id,
        "notAttending",
      );
      const notAttendingSnap = await getDocs(notAttendingSnapRef);

      const attending = attendingSnap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      const notAttending = notAttendingSnap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));

      context.state.activeEvent.data.attending = attending;
      context.state.activeEvent.data.notAttending = notAttending;
      if (payload.callback) payload.callback();
    },

    async addNewUserToTeam(context: any, payload: any) {
      await updateDoc(doc(db, "teams", payload.teamId), {
        [`users.${payload.user.id}`]: {
          name: payload.user.data.name,
          thumbnail: payload.user.data.thumbnail,
          uid: payload.user.id,
          timestamp: serverTimestamp(),
        },
      });

      if (payload.callback) payload.callback();
    },

    async deleteTeam(
      context: any,
      payload: { id: string; callback?: () => void },
    ) {
      await deleteDoc(doc(collection(db, "teams"), payload.id));
      await store.dispatch("fetchTeams");
      if (payload.callback) payload.callback();
    },

    // SONGS

    async getSongs(
      context: any,
      payload: { user: any; callback?: () => void },
    ) {
      const qRef = query(
        collection(db, "songs"),
        orderBy("title"),
        where("account", "==", Number(store.getters["users/user"].account)),
      );

      const querySnapshot = await getDocs(qRef);
      const d: any[] = [];
      querySnapshot.forEach((dSnap) => {
        d.push({
          songId: dSnap.id,
          ...dSnap.data(),
        });
      });
      context.commit("SET_SONGS", d);
      if (payload.callback) payload.callback();
    },

    async fetchCursors(context: any, payload: any) {
      const qRef = query(
        collection(db, "cursors"),
        where("account", "==", Number(store.getters["users/user"].account)),
        where("collection", "==", payload.collection),
        where("sortField", "==", payload.field),
      );
      const snapshot = await getDocs(qRef);
      if (!snapshot.empty) {
        const d = snapshot.docs[0];
        const bundle = {
          cursor: d.data(),
          collection: payload.collection,
          field: payload.field,
        };
        context.commit("UPDATE_CURSORS", bundle);
      }
      payload?.callback?.();
    },

    async exportCollection(context: any, collectionName: string) {
      const querySnapshot = await getDocs(collection(db, collectionName));
      const docs = querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      // you can do something with docs here if needed
      // console.log(docs)
    },

    // Lists one page of songs. Resolves to whether they were listed: not when
    // a later request overtook this one, or the page has no cursor.
    async fetchSongs(
      context: any,
      payload: {
        page?: number;
        sortField?: string;
        sortOrder?: "asc" | "desc";
        rows?: number;
        // Where the page before this one ended; none for the first page.
        after?: { sortValue: unknown; id: string } | null;
      },
    ): Promise<boolean> {
      const requestVersion = ++songsRequestVersion;
      context.commit("SET_FETCHING_SONGS", true);
      try {
        const userAccount = Number(store.getters["users/user"].account);
        const sortField = payload?.sortField ?? "title_lowercase";
        const sortOrder = payload?.sortOrder ?? "asc";
        const rows = payload?.rows ?? SONGS_PAGE_SIZE;

        let qRef: any = query(
          collection(db, "songs").withConverter(songConverter),
          where("account", "==", userAccount),
          orderBy(sortField, sortOrder),
          orderBy(documentId(), "asc"),
          limit(rows),
        );

        if (payload?.after) {
          qRef = query(
            qRef,
            startAfter(payload.after.sortValue, payload.after.id),
          );
        } else if ((payload?.page ?? 0) > 0) {
          // Without its cursor a later page can not be found, and the first
          // page must not be shown under its number.
          return false;
        }

        const snap = await getDocs(qRef);

        // A page asked for since then is the one to show.
        if (requestVersion !== songsRequestVersion) return false;

        type Song = SongModel & { id: string };

        const songs: Song[] = snap.docs.map((d) => {
          const data = d.data() as SongModel;
          return {
            ...data,
            id: d.id,
          };
        });

        context.commit("SET_SONGS", songs);
        return true;
      } finally {
        if (requestVersion === songsRequestVersion) {
          context.commit("SET_FETCHING_SONGS", false);
        }
      }
    },

    async search(context: any, payload: any) {
      const {
        collection: collName,
        field,
        value,
        callback,
        limit: take = 25,
        caseInsensitive = false,
      } = payload || {};

      const account = Number(store.getters["users/user"].account);
      const raw = (value ?? "").toString().trim();

      if (!raw) {
        context.commit("SET_SONGS_FROM_SEARCH", []);
        if (typeof callback === "function") callback({ items: [], count: 0 });
        return;
      }

      const qField = caseInsensitive ? `${field}_lc` : field;
      const qValue = caseInsensitive ? raw.toLowerCase() : raw;

      const buildQuery = (
        searchField: string,
        searchValue: string,
        max = take,
      ) =>
        query(
          collection(db, collName),
          where("account", "==", account),
          orderBy(searchField),
          startAt(searchValue),
          endAt(searchValue + "\uf8ff"),
          limit(max),
        );

      try {
        let qRef = buildQuery(qField, qValue);
        let snap = await getDocs(qRef);

        if (caseInsensitive && snap.empty) {
          qRef = buildQuery(field, raw);
          snap = await getDocs(qRef);
        }

        const items = snap.docs.map((d) => ({ ...d.data(), id: d.id }));
        context.commit("SET_SONGS_FROM_SEARCH", items);
        if (typeof callback === "function")
          callback({ items, count: items.length });
      } catch (err: any) {
        console.error("searchSongs error:", err);
        context.commit("SET_SONGS_FROM_SEARCH", []);
        if (typeof callback === "function")
          callback({ error: err?.message || "Search failed" });
      }
    },

    async clearSearch(context: any) {
      context.commit("SET_SONGS_FROM_SEARCH", null);
    },

    async fetchSong(
      context: any,
      payload: { id: string; callback?: () => void },
    ) {
      const requestVersion = ++activeSongRequestVersion;
      context.commit("SET_FETCHING_SONG", true);
      try {
        const ref = doc(db, "songs", payload.id).withConverter(songConverter);
        const snap = await getDoc(ref);

        if (requestVersion !== activeSongRequestVersion) return;

        context.commit(
          "SET_SONG",
          snap.exists() ? { ...snap.data(), songId: snap.id } : null,
        );
        if (payload.callback) payload.callback();
      } finally {
        if (requestVersion === activeSongRequestVersion) {
          context.commit("SET_FETCHING_SONG", false);
        }
      }
    },

    async updateSongData(
      context: any,
      payload: { id: string; content: any; callback?: () => void },
    ) {
      const docRef = doc(db, "songs", payload.id);
      await setDoc(
        docRef,
        {
          data: payload.content,
        },
        { merge: true },
      );
      const snap = await getDoc(docRef);
      context.commit("SET_SONG", snap.data());
      if (payload.callback) payload.callback();
    },

    async addSong(context: any, payload: { data: any; callback?: () => void }) {
      const newSongRef = await addDoc(collection(db, "songs"), payload.data);
      const newSongSnapshot = await getDoc(newSongRef);
      if (newSongSnapshot.exists()) {
        const song = { songId: newSongSnapshot.id, ...newSongSnapshot.data() };
        context.commit("SET_SONG", song);
        if (payload.callback) payload.callback();
      }
    },

    async updateActiveSongData(context: any, payload: any) {
      context.commit("UPDATE_ACTIVE_SONG_DATA", payload);
    },

    async updateActiveSongContent(context: any, payload: any) {
      context.commit("UPDATE_ACTIVE_SONG_CONTENT", payload);
    },

    async setActiveSong(context: any, payload: any) {
      activeSongRequestVersion += 1;
      context.commit("SET_FETCHING_SONG", false);
      context.commit("SET_SONG", payload);
    },

    async updateCursors(context: any, payload: any) {
      const account = Number(store.getters["users/user"].account);

      const { lastDocs, totalItems } = await buildPageCursors(
        payload.collection,
        payload.field,
        account,
        SONGS_PAGE_SIZE,
      );
      const cursor = {
        account,
        collection: payload.collection,
        sortField: payload.field,
        pageSize: SONGS_PAGE_SIZE,
        lastDocs,
        totalItems,
      };

      const cursorDocRef = doc(
        db,
        "cursors",
        `${payload.collection}_${payload.field}_${account}`,
      );
      await setDoc(
        cursorDocRef,
        { ...cursor, updatedAt: serverTimestamp() },
        { merge: true },
      );
      // A list on screen pages by the cursors in the store, not by these.
      context.commit("UPDATE_CURSORS", {
        collection: payload.collection,
        field: payload.field,
        cursor,
      });

      payload?.callback?.();
    },

    async updateSongController(context: any, payload: any) {
      const docRef = doc(
        db,
        "events",
        payload.eventId,
        "controllers",
        "currentSong",
      );
      try {
        await setDoc(docRef, payload.data);
        if (payload.callback) payload.callback();
      } catch (error) {
        console.error("Error while updating song: ", error);
      }
    },

    async addUserToEventTeam(context: any, payload: any) {
      const docRef = doc(db, "events", payload.eventId);
      const collectionRef = collection(docRef, "participants");
      await addDoc(collectionRef, {
        name: payload.name,
        uid: payload.uid,
        assignments: payload.assignments,
        designatedTeam: payload.designatedTeam,
        designatedTeamId: payload.designatedTeamId,
        thumbnail: payload.thumbnail,
        teamId: payload.teamId,
        timestamp: serverTimestamp(),
      }).then(() => {
        if (payload.callback) payload.callback();
      });
    },

    async addFeatureToSchedule(context: any, payload: any) {
      const docRef = doc(db, "events", payload.docId);
      const collectionRef = collection(docRef, "schedule");
      await addDoc(collectionRef, {
        title: payload.title,
        description: payload.description ?? null,
        type: "feature",
        start: payload.start,
        timestamp: serverTimestamp(),
      });
      return { success: true };
    },

    async addElementToSchedule(context: any, payload: any) {
      const docRef = doc(db, "events", payload.docId);
      const collectionRef = collection(docRef, "schedule");
      let songRef: any;
      if (payload.songId) {
        songRef = doc(db, "songs", payload.songId);
      }
      await addDoc(collectionRef, {
        reference: songRef ?? null,
        title:
          payload.type == "sermon"
            ? "Sermon"
            : payload.type == "song"
              ? payload.data.title
              : payload.title,
        description: payload.description ?? null,
        songId: payload.type === "song" ? payload.data.songId : null,
        type: payload.type,
        key: payload.type === "song" ? payload.data.key : null,
        start: payload.start,
        timestamp: serverTimestamp(),
      }).then(() => {
        if (payload.callback) payload.callback();
      });
    },

    async updateScheduleElement(context: any, payload: any) {
      const docRef = doc(
        db,
        "events",
        payload.eventId,
        "schedule",
        payload.scheduleId,
      );
      try {
        const obj: any = {};
        if (payload.key) {
          obj.key = payload.key;
        }
        if (payload.start) {
          obj.start = payload.start;
        }
        await updateDoc(docRef, obj);
        context.commit("UPDATE_SCHEDULE_ELEMENT", payload);
        if (payload.callback) payload.callback();
      } catch (error) {
        console.error("Error while updating song: ", error);
      }
    },

    async updateSongInSchedule(context: any, payload: any) {
      const songRef = doc(
        db,
        "events",
        payload.eventId,
        "schedule",
        payload.scheduleId,
      );
      try {
        await updateDoc(songRef, {
          key: payload.key,
        });
        context.commit("UPDATE_ACTIVE_SCHEDULE_SONG_KEY", payload);
        if (payload.callback) payload.callback();
      } catch (error) {
        console.error("Error while updating song: ", error);
      }
    },

    async deleteItemFromSchedule(context: any, payload: any) {
      const docRef = doc(db, "events", payload.docId, "schedule", payload.id);
      await deleteDoc(docRef).then(() => {
        const newSchedule = context.state.activeSchedule.data.filter(
          (obj: any) => obj.id != payload.id,
        );
        context.state.activeSchedule.data = newSchedule;
        if (payload.callback) payload.callback();
      });
    },

    async addScheduleItem(context: any, payload: any) {
      const obj: any = {
        title: payload.data.title,
        type: payload.data.type,
        start: payload.data.start,
        timestamp: serverTimestamp(),
      };
      if (payload.data.songReference) {
        obj.reference = payload.data.songReference;
      }

      const docRef = doc(db, "events", "schedule");
      await setDoc(docRef, obj);

      if (payload.callback) payload.callback();
    },

    async saveScheduleChanges(context: any, payload: any) {
      const collectionRef = collection(
        db,
        "events",
        payload.docId,
        "schedule",
        payload.scheduleId,
      );
      const collectionSnapshot = await getDocs(collectionRef);
    },

    async deleteEvent(
      context: any,
      payload: { id: string; callback?: () => void },
    ) {
      await deleteDoc(doc(collection(db, "events"), payload.id));
      if (payload.callback) payload.callback();
    },

    async setAttending(context: any, payload: any) {
      const user = payload.user;
      if (user) {
        try {
          await updateDoc(doc(db, "events", payload.docId), {
            [payload.attending
              ? `attendees.${user.uid}`
              : `nonAttendees.${user.uid}`]: {
              name: user.name,
              teams: user.teams ?? null,
              skills: user.skills ?? null,
              thumbnail: user.thumbnail ?? "",
              uid: user.uid,
              timestamp: serverTimestamp(),
            },
          });
        } catch (error) {
          console.error(error);
        }
      }
    },

    async removeUserFromAttendingLists(context: any, payload: any) {
      await updateDoc(doc(db, "events", payload.docId), {
        [`attendees.${store.getters["users/user"].uid}`]: deleteField(),
        [`nonAttendees.${store.getters["users/user"].uid}`]: deleteField(),
      });

      if (payload.callback) payload.callback();
    },
  },
  modules: {
    auth: auth,
    events,
    sermon,
    users,
    teams,
    memberships,
    eventTeams,
    roles,
    organisations,
    scheduleTemplates,
    slideshows,
    slideImages,
    songVariants,
    songCatalogue,
  },
});

export default store;
